import React from 'react';
import PropTypes from 'prop-types';

import { withStyles } from '../tools/withStyles';
import { Main } from '../components/Main';
import { Link } from '../components/Link';
import { Text } from '../components/Text';
import { Fader } from '../components/Fader';
import { Secuence } from '../components/Secuence';
import electionImageURL from '../images/elec2022.jpeg';

const styles = theme => ({
  root: {}
});

class Charity extends React.Component {
  static propTypes = {
    classes: PropTypes.object
  };

  render () {
    const { classes } = this.props;

    return (
      <Main className={classes.root}>
        <Secuence stagger>
          <article>
            <header>
              <h1><Text>STORY</Text></h1>
              <h2><Text>The 2022 Philippine Elections: A Defining Moment in Democratic History</Text></h2>
            </header>

            <Fader>
              <img src={electionImageURL} alt="2022 Philippine Elections" style={{ width: '100%', maxWidth: '800px' }} />
            </Fader>

            <p><Text>On May 9, 2022, the Philippines witnessed one of its most consequential elections since the 1986 People Power Revolution. The election would determine not just the country's leadership for the next six years, but potentially reshape the trajectory of Philippine democracy itself. What unfolded was a landslide victory that brought the Marcos dynasty back to Malacañang Palace—exactly 36 years after the family fled into exile during the EDSA Revolution.</Text></p>

            <Fader>
              <h2>The Presidential Race: A Historic Return</h2>

              <p>Ferdinand "Bongbong" Marcos Jr., son of the late dictator Ferdinand Marcos Sr., secured an overwhelming victory with <strong>31,097,403 votes (58.77%)</strong>—more than double that of his closest rival, Vice President Leni Robredo, who garnered <strong>14,812,964 votes (28.04%)</strong>. This victory margin represented the highest vote share gained by any presidential candidate since his father's controversial 1969 re-election.</p>

              <p>The geographic distribution of votes revealed a dramatic shift in the political landscape. Marcos Jr. won in <strong>64 out of 81 provinces</strong>, while Robredo carried only two regions: her bailiwick Bicol and Western Visayas. Notably, the vote-rich province of Cebu contributed <strong>1,515,812 votes</strong> to Marcos—a significant flip from the 2016 vice-presidential race where Robredo had prevailed.</p>

              <p>This result marked a complete reversal from the 2016 vice-presidential contest between the two candidates, where Robredo had narrowly defeated Marcos by just 263,473 votes (14,418,817 to 14,155,344). The dramatic swing—from a razor-thin defeat to a landslide victory in just six years—would become a subject of intense analysis and debate about the role of disinformation, historical revisionism, and evolving political dynamics.</p>

              <blockquote>
                <p><em>"Almost exactly 50 years after Ferdinand Marcos declared martial law and established a dictatorship in the country, his namesake son secured more than 30 million votes in the May 9 presidential election, nearly double that of his nearest rival."</em></p>
                <p style={{ fontSize: '0.9em' }}>— Wilson Center Analysis</p>
              </blockquote>

              <h3>Other Presidential Candidates</h3>
              <p>While Marcos and Robredo dominated the race, several other candidates competed for the presidency:</p>
              <ul>
                <li><strong>Manny Pacquiao</strong> (3,625,867 votes / 6.86%) - Boxing legend and Senator</li>
                <li><strong>Francisco "Isko Moreno" Domagoso</strong> (1,952,901 votes / 3.70%) - Manila Mayor</li>
                <li><strong>Panfilo Lacson</strong> (905,548 votes / 1.71%) - Former Senator and PNP Chief</li>
              </ul>
            </Fader>

            <Fader>
              <h2>The Vice Presidential Race: Sara Duterte's Commanding Victory</h2>

              <p>Running alongside Marcos Jr. in the "UniTeam" alliance, Sara Duterte-Carpio, daughter of outgoing President Rodrigo Duterte, won an even more decisive victory in the vice-presidential race. She secured <strong>31,554,816 votes (61.53%)</strong>, establishing several historic records:</p>

              <ul>
                <li>First vice president elected by a majority since the 1986 elections</li>
                <li>Largest majority margin for a vice president since 1969</li>
                <li>Most votes ever received by any candidate in a single-winner election in Philippine history</li>
              </ul>

              <p>Her closest rival, Senator Francis "Kiko" Pangilinan (Robredo's running mate), received only <strong>9,227,797 votes (17.64%)</strong>—a margin of over 22 million votes. Other vice-presidential candidates included:</p>
              <ul>
                <li><strong>Tito Sotto</strong> (8,252,053 votes / 15.77%)</li>
                <li><strong>Willie Ong</strong> (2,433,582 votes / 4.65%)</li>
              </ul>

              <p>The Marcos-Duterte tandem's combined victory signified the consolidation of two powerful political dynasties, uniting the support bases of both the Marcos loyalists and Duterte's populist following.</p>
            </Fader>

            <Fader>
              <h2>The Senatorial Race: A Mixed Field of Winners</h2>

              <p>The senatorial race produced a diverse slate of 12 winners, representing a mixture of political alignments, though candidates associated with the UniTeam alliance won a plurality of seats:</p>

              <h3>Top 12 Senators (2022-2028 Term)</h3>
              <ol>
                <li><strong>Robin Padilla</strong> — 26,612,434 votes (First-time senator, highest vote-getter)</li>
                <li><strong>Loren Legarda</strong> — 24,264,969 votes (Returning senator)</li>
                <li><strong>Raffy Tulfo</strong> — 23,396,954 votes (First-time senator)</li>
                <li><strong>Sherwin Gatchalian</strong> — 20,602,655 votes (Re-elected incumbent)</li>
                <li><strong>Francis "Chiz" Escudero</strong> — 20,271,458 votes (Returning senator)</li>
                <li><strong>Mark Villar</strong> — 19,475,592 votes (First-time senator)</li>
                <li><strong>Alan Peter Cayetano</strong> — 19,295,314 votes (Returning senator)</li>
                <li><strong>Juan Miguel Zubiri</strong> — 18,734,336 votes (Re-elected incumbent)</li>
                <li><strong>Joel Villanueva</strong> — 18,486,034 votes (Re-elected incumbent)</li>
                <li><strong>JV Ejercito</strong> — 15,841,858 votes (Returning senator)</li>
                <li><strong>Risa Hontiveros</strong> — 15,420,807 votes (Re-elected incumbent, lone opposition senator)</li>
                <li><strong>Jinggoy Estrada</strong> — 15,108,625 votes (Returning senator)</li>
              </ol>

              <p>Notably, actor Robin Padilla, a first-time candidate, secured the highest number of votes with approximately 48% of the electorate's support. Only <strong>one opposition candidate</strong>, Senator Risa Hontiveros, secured a seat—highlighting the dominance of administration-aligned candidates. Three candidates from the Robredo-Pangilinan slate won seats, while the majority aligned with UniTeam or were administration-backed.</p>

              <p>The senatorial results included four successfully re-elected incumbents (Gatchalian, Zubiri, Villanueva, Hontiveros), five returning former senators (Legarda, Escudero, Cayetano, Ejercito, Estrada), and three first-time senators (Padilla, Tulfo, Villar).</p>
            </Fader>

            <Fader>
              <h2>Why This Election Matters: Significance to the Philippines Today</h2>

              <h3>1. The Return of the Marcos Dynasty</h3>
              <p>The election represented what political analysts describe as a "counterrevolution"—the successful return to power of a family that was ousted through a popular uprising. This outcome raised profound questions about historical memory, justice, and the resilience of democratic institutions. The Marcos Sr. regime (1965-1986) was marked by:</p>
              <ul>
                <li>Declaration of Martial Law (1972-1981) and authoritarian rule until 1986</li>
                <li>Documented human rights abuses: 3,257 extrajudicial killings, 35,000 documented tortures, and 70,000 incarcerations</li>
                <li>Economic plunder: An estimated $5-10 billion stolen from the Philippine treasury</li>
                <li>The Philippine Supreme Court finding the Marcos family guilty of ill-gotten wealth</li>
              </ul>

              <p>The 2022 victory came despite these historical records, raising questions about collective memory, generational shifts, and the effectiveness of democratic safeguards.</p>

              <h3>2. The Role of Digital Disinformation and Historical Revisionism</h3>
              <p>Multiple academic studies and fact-checking organizations documented an extensive disinformation campaign that reshaped historical narratives about the Marcos era. According to research from the Wilson Center, PBS, and Journal of Democracy:</p>

              <blockquote>
                <p><em>"Historical memories which could have prevented the return of another Marcos to power were lost to an assiduous and systematic revisionist campaign successfully orchestrated by the Marcos team, using different social media platforms."</em></p>
              </blockquote>

              <p>This phenomenon demonstrated how digital platforms could be weaponized to rewrite history, presenting the Martial Law period as a "golden age" rather than a period of repression—a narrative that particularly resonated with younger voters who had no direct experience of that era.</p>

              <h3>3. Democratic Erosion Concerns</h3>
              <p>Democracy watchdogs and international observers expressed alarm about the implications for Philippine democracy. Key concerns included:</p>
              <ul>
                <li><strong>Institutional Weakening:</strong> The potential for a new constitution that could defang anti-corruption bodies and weaken checks and balances</li>
                <li><strong>Press Freedom:</strong> Continued threats to independent journalism, building on restrictions from the previous administration</li>
                <li><strong>Information Integrity:</strong> The erosion of shared factual foundations necessary for democratic deliberation</li>
                <li><strong>Human Rights:</strong> Fears that abuses from the previous Duterte administration would continue or worsen</li>
              </ul>

              <p>As PBS News noted: <em>"The new Marcos government may accelerate Philippine democracy's decay, as the country's democratic institutions have already been battered by six years of the Duterte presidency and the rise of online disinformation."</em></p>

              <h3>4. Current Governance Issues and Policy Challenges</h3>
              <p>The Marcos Jr. administration has faced several significant challenges and controversies since taking office:</p>

              <h4>Infrastructure Corruption Scandals</h4>
              <p>The flood control infrastructure program has become emblematic of governance concerns. Between July 2022 and May 2025, the administration allocated <strong>₱545.6 billion ($11.08 billion)</strong> for flood mitigation, completing approximately 9,800-9,855 projects. However:</p>
              <ul>
                <li><strong>Contractor Concentration:</strong> Only 15 out of 2,409 accredited contractors received ₱100 billion (18%) of the total budget</li>
                <li><strong>Alleged Kickbacks:</strong> Senator Erwin Tulfo called the program "a grand robbery," alleging kickbacks as high as 25%</li>
                <li><strong>Questionable Effectiveness:</strong> Despite ₱26 billion spent on flood control in some areas, severe flooding continues</li>
                <li><strong>Top Beneficiaries:</strong> Sunwest Inc. alone received ₱10 billion in contracts; companies linked to the Discayas family cornered over ₱31 billion</li>
              </ul>

              <p>In response to mounting criticism, President Marcos issued Executive Order No. 94 in September 2025, creating an Independent Commission for Infrastructure to investigate these projects and ordered the reallocation of ₱255.5 billion originally intended for 2026 flood control projects.</p>

              <h4>Other Policy Areas</h4>
              <ul>
                <li><strong>Economic Management:</strong> Addressing inflation, unemployment, and post-pandemic recovery</li>
                <li><strong>Foreign Relations:</strong> Navigating tensions with China over West Philippine Sea disputes</li>
                <li><strong>Agriculture:</strong> Food security concerns (Marcos serves as his own Agriculture Secretary)</li>
                <li><strong>Energy Crisis:</strong> Power supply stability and transition to renewable energy</li>
              </ul>
            </Fader>

            <Fader>
              <h2>EleksyonPH: Visualizing Democracy Through Data</h2>

              <p>Understanding the 2022 election and its ongoing implications requires more than just looking at final vote tallies. <strong>EleksyonPH</strong> was created to provide citizens, researchers, journalists, and policymakers with the tools to explore the granular details of Philippine electoral democracy and understand how voting patterns correlate with governance outcomes.</p>

              <h3>How EleksyonPH Shows Visual Correlations</h3>

              <h4>1. Electoral Geography and Infrastructure Spending</h4>
              <p>By overlaying election results with infrastructure project data at the provincial, municipal, and barangay levels, users can explore questions such as:</p>
              <ul>
                <li>Do areas with higher vote margins for the administration receive proportionally more flood control projects?</li>
                <li>Is there a correlation between swing provinces (those that flipped from Robredo in 2016 to Marcos in 2022) and infrastructure allocation?</li>
                <li>How do contractor concentrations map onto electoral strongholds?</li>
                <li>Which barangays that experienced severe flooding despite infrastructure spending voted for or against the current administration?</li>
              </ul>

              <h4>2. Multi-Scale Analysis Across Administrative Levels</h4>
              <p>EleksyonPH operates in three dynamic modes—Provincial, Municipal, and Barangay—allowing users to:</p>
              <ul>
                <li><strong>Zoom from National to Hyperlocal:</strong> Start with provincial trends and drill down to individual barangay voting patterns</li>
                <li><strong>Compare Across Scales:</strong> Understand whether national trends hold at local levels or if there are significant variations</li>
                <li><strong>Cross-Reference Data:</strong> Match election results with socioeconomic indicators, disaster vulnerability, infrastructure projects, and other datasets</li>
              </ul>

              <h4>3. Interactive Data Exploration</h4>
              <p>The platform provides:</p>
              <ul>
                <li><strong>Dynamic Choropleth Maps:</strong> Visualize vote shares, margins, turnout rates, and other electoral metrics with customizable color schemes</li>
                <li><strong>Popup Charts and Statistics:</strong> Click on any province, municipality, or barangay to see detailed breakdowns of votes by candidate, comparisons with previous elections, and demographic data</li>
                <li><strong>Search and Filter Tools:</strong> Quickly locate specific administrative units or filter by various criteria (vote margin thresholds, candidate performance, etc.)</li>
                <li><strong>Comparative Timelines:</strong> Compare 2016 vs. 2022 results to identify geographic shifts and trends</li>
              </ul>

              <h4>4. Correlation with Contemporary Issues</h4>
              <p>Beyond flood control, EleksyonPH aims to integrate multiple datasets to show visual correlations between election results and:</p>
              <ul>
                <li><strong>Poverty Incidence:</strong> Do economically disadvantaged areas vote differently?</li>
                <li><strong>Educational Attainment:</strong> How do literacy rates and educational levels correlate with voting patterns?</li>
                <li><strong>Disaster Vulnerability:</strong> Are disaster-prone areas receiving adequate infrastructure investment?</li>
                <li><strong>Agricultural Productivity:</strong> How do farming communities vote, and how does that correlate with agricultural policy outcomes?</li>
                <li><strong>Healthcare Access:</strong> Mapping health facility distribution against political support patterns</li>
                <li><strong>Disinformation Exposure:</strong> Where possible, correlating social media penetration with shifts in voting behavior</li>
              </ul>

              <h4>5. Accountability and Transparency</h4>
              <p>By making this data publicly accessible without authentication barriers, EleksyonPH serves as a transparency tool that enables:</p>
              <ul>
                <li><strong>Investigative Journalism:</strong> Reporters can identify patterns worthy of deeper investigation</li>
                <li><strong>Academic Research:</strong> Scholars can study political geography, voting behavior, and governance outcomes</li>
                <li><strong>Civil Society Monitoring:</strong> Citizens and advocacy groups can track whether government resources are distributed equitably</li>
                <li><strong>Electoral Integrity:</strong> Anomalous patterns can be flagged for further scrutiny</li>
              </ul>

              <blockquote>
                <p><em>"Democracy dies in darkness. Data-driven transparency platforms like EleksyonPH ensure that citizens have the tools to hold their government accountable and understand the true state of their nation's democracy."</em></p>
              </blockquote>

              <h3>Example Use Cases</h3>

              <h4>Case Study 1: The Cebu Flip</h4>
              <p>Cebu Province provided the most votes to Marcos (1.5M+) despite voting for Robredo in the 2016 VP race. Using EleksyonPH, users can:</p>
              <ul>
                <li>Examine which municipalities within Cebu experienced the largest swings</li>
                <li>Overlay this with infrastructure project allocations post-2022</li>
                <li>Compare with socioeconomic changes between 2016-2022</li>
                <li>Identify potential factors (social media campaigns, local political shifts, etc.)</li>
              </ul>

              <h4>Case Study 2: Flood Control in Vote-Rich Areas</h4>
              <p>By mapping the ₱545.6B in flood control spending against electoral performance:</p>
              <ul>
                <li>Identify whether administration strongholds receive disproportionate investment</li>
                <li>Examine opposition-voting areas that continue to flood despite national spending</li>
                <li>Track contractor activity geographically and correlate with local political dynamics</li>
                <li>Assess whether spending follows actual flood vulnerability or political considerations</li>
              </ul>

              <h4>Case Study 3: The Pink Provinces</h4>
              <p>Robredo won only in Bicol and Western Visayas. EleksyonPH allows exploration of:</p>
              <ul>
                <li>What makes these regions different from the rest of the country?</li>
                <li>How have they fared under the current administration in terms of development projects?</li>
                <li>Are there measurable differences in governance outcomes in these provinces?</li>
              </ul>
            </Fader>

            <Fader>
              <h2>Looking Forward: The Role of Data in Democratic Resilience</h2>

              <p>The 2022 Philippine elections demonstrated both the fragility and importance of democratic institutions in the digital age. Disinformation, historical revisionism, and weakened institutional checks created conditions for the return of a dynasty many thought would never regain power.</p>

              <p>Yet democracy's survival depends on an informed citizenry with access to accurate information and analytical tools. EleksyonPH represents a commitment to transparency, accountability, and data-driven civic engagement. By making complex electoral and governance data accessible and visualizable, the platform empowers Filipinos to:</p>

              <ul>
                <li>Understand their political landscape with unprecedented granularity</li>
                <li>Track whether campaign promises translate into equitable development</li>
                <li>Identify patterns of resource distribution that merit scrutiny</li>
                <li>Make more informed decisions in future elections</li>
                <li>Hold elected officials accountable to their constituents</li>
              </ul>

              <p>The story of the 2022 elections is still being written. The full implications of the Marcos restoration will unfold over years and decades. But with tools like EleksyonPH, citizens need not be passive observers of their own political fate. Instead, they can actively engage with data, ask difficult questions, and demand answers—essential practices for any democracy seeking to survive and thrive.</p>

              <blockquote>
                <p><em>"A functioning democracy requires not just free elections, but informed voters, transparent governance, and accountable institutions. EleksyonPH contributes to all three."</em></p>
              </blockquote>

              <p style={{ marginTop: '2rem', fontSize: '0.9em', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '1rem' }}>
                <strong>Sources and References:</strong><br/>
                Commission on Elections (COMELEC) Official Results •
                <Link href='https://www.rappler.com' target='rappler'> Rappler</Link> •
                <Link href='https://www.philstar.com' target='philstar'> Philippine Star</Link> •
                <Link href='https://newsinfo.inquirer.net' target='inquirer'> Philippine Daily Inquirer</Link> •
                <Link href='https://www.wilsoncenter.org' target='wilson'> Wilson Center</Link> •
                <Link href='https://www.pbs.org' target='pbs'> PBS News</Link> •
                <Link href='https://www.brookings.edu' target='brookings'> Brookings Institution</Link> •
                Philippine Center for Investigative Journalism (PCIJ) •
                Commission on Audit (COA) Performance Reports
              </p>

              <p style={{ margin: 0, fontSize: '0.85em', fontStyle: 'italic' }}>Last updated: November 2025</p>
            </Fader>
          </article>
        </Secuence>
      </Main>
    );
  }
}

export default withStyles(styles)(Charity);
